import { PropInstanceHorizontalAlignment } from "building-types/datums/datumsTypesEnum";
import type { Prop } from "building-types/props/propTypes";
import type { Room } from "building-types/rooms/roomTypes";
import { RoomType } from "building-types/rooms/roomTypesEnum";
import { PropCatalogType } from "building-types/settings/settingsTypesEnum";
import { WallType } from "building-types/walls/wallEnums";
import type { WallIndex } from "building-types/walls/wallIndex";
import type { WallStrip } from "building-types/walls/wallStrip";
import { BED_FULL_WIDTH, BED_KING_WIDTH, BED_QUEEN_WIDTH, BED_TWIN_WIDTH } from "common/constants/buildingObjects";
import { ensureDefined } from "common/utils/assertOfType";
import { BuiltInInteriorPropId } from "doc/elements/propCatalog/interiorPropCatalog";
import { defaultPropLabels } from "doc/propTools";
import { propOffsetFromLine2 } from "doc/rooms/propPlacement";
import { polyLine2EdgeLength } from "geom/polyLine2";
import { getWallPath, wallCenterLineAsPolyLine2, wallIsBoundary } from "../walls/wall";
import { WallStripImpl } from "../walls/wallStripImpl";
import type { RoomLayoutFunc, RoomLayoutGenerator } from "./roomLayout";
import { overloadIndex } from "./roomLayout";

const bedLayout: RoomLayoutFunc = async ({ room, palette, wallIndex }) => {
  const props: Prop[] = [];

  // eslint-disable-next-line prefer-const
  let { bedIndex, entryIndex, closetIndex, bedStrip, strips } = bedroomWallStripIndices(room, wallIndex);

  if (bedIndex === -1) {
    bedIndex = overloadIndex(entryIndex - 1);
  }

  if (bedIndex === entryIndex) {
    bedIndex = overloadIndex(entryIndex - 1);
  }

  if (bedIndex === closetIndex) {
    bedIndex = overloadIndex(bedIndex + 1);
  }

  if (bedIndex === entryIndex) {
    bedIndex = overloadIndex(entryIndex + 2);
  }

  bedStrip = strips[overloadIndex(bedIndex)];
  if (!bedStrip) {
    return {};
  }

  const bedBaseline = bedStrip.baseline(true);
  const bedWallLen = polyLine2EdgeLength(bedBaseline, 0);

  let propId: BuiltInInteriorPropId;
  let sideTableOffset = 0;

  if (bedWallLen > 150) {
    propId = BuiltInInteriorPropId.BEDKING;
    sideTableOffset = BED_KING_WIDTH / 2;
  } else if (bedWallLen > 130) {
    propId = BuiltInInteriorPropId.BEDQUEEN;
    sideTableOffset = BED_QUEEN_WIDTH / 2;
  } else if (bedWallLen > 110) {
    propId = BuiltInInteriorPropId.BEDFULL;
    sideTableOffset = BED_FULL_WIDTH / 2;
  } else if (bedWallLen > 100) {
    propId = BuiltInInteriorPropId.BEDTWIN;
    sideTableOffset = BED_TWIN_WIDTH / 2;
  } else {
    return {};
  }

  const bed = await propOffsetFromLine2({
    settings: palette,
    propId: propId,
    room,
    offset: 2,
    edge: bedBaseline,
    z: room.level.floorElevation,
    param: bedWallLen / 2,
    paramNormalized: false,
    horizontalAlignment: PropInstanceHorizontalAlignment.CENTER,
    catalogType: PropCatalogType.PROP_CATALOG,
    labels: defaultPropLabels(),
  });

  props.push(bed);

  if (bedWallLen > sideTableOffset * 2 + 80) {
    sideTableOffset += 14;
    const side1 = await propOffsetFromLine2({
      settings: palette,
      propId: BuiltInInteriorPropId.ENDTABLE,
      room,
      offset: 2,
      edge: bedBaseline,
      z: room.level.floorElevation,
      param: bedWallLen / 2 + sideTableOffset,
      paramNormalized: false,
      horizontalAlignment: PropInstanceHorizontalAlignment.CENTER,
      catalogType: PropCatalogType.PROP_CATALOG,
      labels: defaultPropLabels(),
    });

    const side2 = await propOffsetFromLine2({
      settings: palette,
      propId: BuiltInInteriorPropId.ENDTABLE,
      room,
      offset: 2,
      edge: bedBaseline,
      z: room.level.floorElevation,
      param: bedWallLen / 2 - sideTableOffset,
      paramNormalized: false,
      horizontalAlignment: PropInstanceHorizontalAlignment.CENTER,
      catalogType: PropCatalogType.PROP_CATALOG,
      labels: defaultPropLabels(),
    });
    props.push(side1, side2);
  }

  return { props };
};

const roomLayoutBedroom: RoomLayoutGenerator[] = [
  {
    roomType: RoomType.BEDROOM,
    generate: bedLayout,
  },
  {
    roomType: RoomType.MASTER_BED,
    generate: bedLayout,
  },
  {
    roomType: RoomType.GUEST_SUITE,
    generate: bedLayout,
  },
  {
    roomType: RoomType.MASTER_SEAT,
    async generate({ room, wallIndex, palette }) {
      const voidWallGroups = wallIndex.wallStripsByPredicate(room, (wall) => wall.wallType === WallType.VOID, true);
      const voidWallLines = WallStripImpl.allEndPoints(voidWallGroups);

      const noVoidWallGroups = wallIndex.wallStripsByPredicate(room, (wall) => wall.wallType === WallType.SOLID, true);
      const noVoidWallLines = WallStripImpl.allEndPoints(noVoidWallGroups);

      noVoidWallLines.sort((a, b) => polyLine2EdgeLength(b, 0) - polyLine2EdgeLength(a, 0));
      voidWallLines.sort((a, b) => polyLine2EdgeLength(b, 0) - polyLine2EdgeLength(a, 0));

      const rwalls = wallIndex.wallsInRoom(room);
      const windowWalls = rwalls.filter((wall) => wall.wallType === WallType.SOLID && wall.windowPlacements.length > 0);
      windowWalls.sort((a, b) => getWallPath(b).length - getWallPath(a).length);

      const props: Prop[] = [];

      const hostWall = windowWalls[0];
      let hostLine;

      // place seating based on wall size

      if (hostWall) {
        hostLine = wallCenterLineAsPolyLine2(room, hostWall);
      } else {
        return null;
      }

      const wallLength = polyLine2EdgeLength(hostLine, 0);

      let propId: BuiltInInteriorPropId | undefined;

      if (wallLength > 150) {
        propId = BuiltInInteriorPropId.LOVESEAT;
      } else if (wallLength > 100) {
        propId = BuiltInInteriorPropId.ARMCHAIR;
      } else if (wallLength > 75) {
        propId = BuiltInInteriorPropId.ARMCHAIR;
      }

      if (propId) {
        const chair = await propOffsetFromLine2({
          settings: palette,
          propId,
          room,
          offset: 8,
          edge: hostLine,
          param: 0.5,
          paramNormalized: true,
          z: room.level.floorElevation,
          horizontalAlignment: PropInstanceHorizontalAlignment.CENTER,
          catalogType: PropCatalogType.PROP_CATALOG,
          labels: defaultPropLabels(),
        });

        props.push(chair);
      }

      const table = await propOffsetFromLine2({
        settings: palette,
        propId: BuiltInInteriorPropId.COFFEETABLE,
        room,
        offset: 54,
        edge: hostLine,
        param: 0.5,
        paramNormalized: true,
        z: room.level.floorElevation,
        horizontalAlignment: PropInstanceHorizontalAlignment.CENTER,
        catalogType: PropCatalogType.PROP_CATALOG,
        labels: defaultPropLabels(),
      });

      props.push(table);

      return {
        props,
        casework: [],
      };
    },
  },
];

function bedroomWallStripIndices(
  room: Room,
  wallIndex: WallIndex,
): {
  strips: WallStrip[];
  bedIndex: number;
  bedStrip: WallStrip;
  entryIndex: number;
  entryStrip: WallStrip;
  closetIndex: number;
  closetStrip: WallStrip;
  solidIndex: number;
  solidStrip: WallStrip;
} {
  const strip0 = wallIndex.wallStripFromRoomEdgeIndex(room, 0);
  const strip1 = wallIndex.wallStripFromRoomEdgeIndex(room, 1);
  const strip2 = wallIndex.wallStripFromRoomEdgeIndex(room, 2);
  const strip3 = wallIndex.wallStripFromRoomEdgeIndex(room, 3);
  const strip4 = wallIndex.wallStripFromRoomEdgeIndex(room, 4);
  const strip5 = wallIndex.wallStripFromRoomEdgeIndex(room, 5);

  const strips: WallStrip[] = [strip0, strip1, strip2, strip3, strip4, strip5].filter((x) => !!x);

  // identify the best place for the bed wall.
  let bedIndex = -1;
  let bedStrip;

  let entryIndex = -1;
  let entryStrip;

  let closetIndex = -1;
  let closetStrip;

  let solidIndex = -1;
  let solidStrip;

  let wallLength = 0;

  for (let i = 0; i < strips.length; i++) {
    if (!strips[i]) {
      continue;
    }
    for (const wall of strips[i].walls) {
      if (wall.wallType === WallType.SOLID) {
        const path = getWallPath(wall);
        if (path.length > wallLength && wallIsBoundary(wall)) {
          bedIndex = i;
          bedStrip = strips[i];
          wallLength = path.length;
        }
        if (wall.doorPlacements.length > 0) {
          if (ensureDefined(path.rightRoom).type === RoomType.HALL) {
            entryIndex = i;
            entryStrip = strips[i];
          } else if (
            path.rightRoom &&
            (path.rightRoom.type === RoomType.CLOSET ||
              path.rightRoom.type === RoomType.REACH_CLOSET ||
              path.rightRoom.type === RoomType.WALK_IN_CLOSET)
          ) {
            closetIndex = i;
            closetStrip = strips[i];
          } else if (wall.doorPlacements.length === 0) {
            const host = strips[i].baseline(true);
            if (polyLine2EdgeLength(host, 0) > 108) {
              solidIndex = i;
              solidStrip = strips[i];
            }
          }
        }
      }
    }
  }

  return { strips, entryIndex, entryStrip, bedIndex, solidIndex, closetIndex, bedStrip, closetStrip, solidStrip };
}

export default roomLayoutBedroom;
